import { type ReactNode, useMemo, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { ChevronDown, Plus, Sparkles, Star, Trash } from "lucide-react";

import BaseDialog from "@/components/Modals/BaseDialog";
import { SpotToast } from "@/components/SpotToast";
import { Badge } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/shadcn/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { NoxusMenu } from "@/components/UI/NoxusMenu";
import {
  RichTextValue,
  getTemplateStringValue,
} from "@/components/Form/RichTextField";
import { AgentLanguageInput, NoxusTipTapDocumentInput } from "@/generated";
import { LanguageOption } from "@/types/api/spotflow/spotflow-config";
import { translateIntroMessage } from "@/services/translateIntro";
import { useSpotUser } from "@/utils/spotUser";
import { cn } from "@/lib/utils";

import { ExpandableTextarea } from "./ExpandableTextarea";
import { LanguageChip, describeLanguage, getIntroText } from "./languageUtils";

const REORDER_TRANSITION = {
  duration: 0.28,
  ease: [0.22, 1, 0.36, 1],
} as const;
const ACCORDION_TRANSITION = {
  duration: 0.22,
  ease: [0.22, 1, 0.36, 1],
} as const;

export const LanguagesDialog = ({
  isOpen,
  onOpenChange,
  languages,
  onLanguagesChange,
  defaultLanguage,
  onDefaultLanguageChange,
  options,
  addableCodes,
  introPlaceholder,
  isDisabled,
  notice,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  languages: AgentLanguageInput[];
  onLanguagesChange: (languages: AgentLanguageInput[]) => void;
  defaultLanguage: string | null;
  onDefaultLanguageChange: (code: string | null) => void;
  options: LanguageOption[];
  addableCodes: string[];
  introPlaceholder?: string | null;
  isDisabled?: boolean;
  /** Shown above the list, e.g. the deployment's missing-language warning. */
  notice?: ReactNode;
}) => {
  const { spotGroup } = useSpotUser();
  const [expandedCode, setExpandedCode] = useState<string | null>(
    defaultLanguage ?? languages[0]?.code ?? null
  );
  const [translatingCodes, setTranslatingCodes] = useState<Set<string>>(
    () => new Set()
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const optionByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options]
  );

  const takenCodes = useMemo(
    () => new Set<string>(languages.map((language) => language.code)),
    [languages]
  );
  // Only the widget's languages can be added; a language configured before
  // the restriction still lists (and can be removed) above.
  const availableOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          addableCodes.includes(option.value) && !takenCodes.has(option.value)
      ),
    [options, addableCodes, takenCodes]
  );

  // The default is an explicit field, not a position — but it still reads
  // first, so the list is ordered for display only.
  const ordered = useMemo(
    () =>
      [...languages].sort(
        (a, b) =>
          Number(b.code === defaultLanguage) -
          Number(a.code === defaultLanguage)
      ),
    [languages, defaultLanguage]
  );

  const defaultIntro = getIntroText(
    languages.find((language) => language.code === defaultLanguage)
  ).trim();

  const add = (code: string) => {
    if (isDisabled) return;
    onLanguagesChange([...languages, { code }]);
    if (languages.length === 0) onDefaultLanguageChange(code);
    setExpandedCode(code);
  };

  const remove = (code: string) => {
    if (isDisabled) return;
    const remaining = languages.filter((language) => language.code !== code);
    if (expandedCode === code) setExpandedCode(null);
    onLanguagesChange(remaining);
    if (code === defaultLanguage) {
      onDefaultLanguageChange(remaining[0]?.code ?? null);
    }
  };

  const setIntro = (code: string, intro: RichTextValue) => {
    if (isDisabled) return;
    onLanguagesChange(
      languages.map((language) =>
        language.code === code
          ? {
              ...language,
              introductory_message: [intro as NoxusTipTapDocumentInput],
            }
          : language
      )
    );
  };

  const translate = async (code: string, label: string) => {
    if (isDisabled || !defaultIntro) return;
    setTranslatingCodes((current) => new Set(current).add(code));
    try {
      const translated = await translateIntroMessage({
        groupId: spotGroup.id,
        text: defaultIntro,
        targetLanguage: label,
      });
      setIntro(code, getTemplateStringValue(translated));
      setExpandedCode(code);
    } catch (error) {
      SpotToast.error(`Couldn't translate the message into ${label}.`);
      Sentry.captureException(error);
    } finally {
      setTranslatingCodes((current) => {
        const next = new Set(current);
        next.delete(code);
        return next;
      });
    }
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Welcome message per language"
      description="The welcome message language follows the deployment's language or the visitor's browser language, when available. Otherwise, the default language is used."
      size="2xl"
      descriptionClassName="max-w-none"
      // Overrides BaseDialog's max-h-[90%]: the list scrolls, so a tall
      // viewport shouldn't stretch the dialog to fill it.
      className="max-h-[min(90%,600px)]"
      footerActions={[
        { label: "Done", props: { onClick: () => onOpenChange(false) } },
      ]}
    >
      <LayoutGroup>
        <div className="flex flex-col gap-2">
          {notice}
          {ordered.map((language) => {
            const { label, tag } = describeLanguage(language, optionByValue);
            const isDefault = language.code === defaultLanguage;
            const isExpanded = expandedCode === language.code;
            const preview = getIntroText(language).trim();
            const canTranslate = !isDefault && defaultIntro.length > 0;

            return (
              <motion.div
                key={language.code}
                layout="position"
                transition={REORDER_TRANSITION}
                className="group relative flex w-full flex-col overflow-hidden rounded-lg border border-border bg-background"
              >
                <div className="flex items-center gap-2 p-2">
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-controls={`language-intro-${language.code}`}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                    onClick={() =>
                      setExpandedCode(isExpanded ? null : language.code)
                    }
                  >
                    <span className="relative h-7 w-7 shrink-0">
                      <LanguageChip
                        tag={tag}
                        className="absolute inset-0 transition-opacity group-hover:opacity-0"
                      />
                      <span className="absolute inset-0 flex items-center justify-center rounded-md bg-accent text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
                        <ChevronDown
                          className={cn(
                            "h-3.5 w-3.5 transition-transform",
                            isExpanded && "rotate-180"
                          )}
                        />
                      </span>
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-sm">
                      {label}
                    </span>
                    {!isExpanded && (
                      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                        {preview || "No welcome message"}
                      </span>
                    )}
                  </button>

                  {isDefault && <Badge variant="secondary">Default</Badge>}

                  {canTranslate && (
                    <Button
                      variant="ghost"
                      size="xs"
                      isDisabled={isDisabled}
                      loading={translatingCodes.has(language.code)}
                      loadingText="Translating"
                      onClick={() => translate(language.code, label)}
                    >
                      <Sparkles size={14} />
                      Translate
                    </Button>
                  )}

                  <NoxusMenu
                    isSmall
                    isDisabled={isDisabled}
                    itemsArray={[
                      {
                        label: "Make default",
                        icon: Star,
                        isHidden: isDefault,
                        onClick: () => onDefaultLanguageChange(language.code),
                      },
                      {
                        label: "Remove",
                        icon: Trash,
                        color: "destructive",
                        onClick: () => remove(language.code),
                      },
                    ]}
                  />
                </div>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      key="intro"
                      id={`language-intro-${language.code}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={ACCORDION_TRANSITION}
                      className="overflow-hidden"
                    >
                      <ExpandableTextarea
                        defaultExpanded
                        isFlush
                        value={language.introductory_message?.[0] ?? ""}
                        onChange={(intro) => setIntro(language.code, intro)}
                        isDisabled={isDisabled}
                        label="Welcome message"
                        placeholder={
                          introPlaceholder ?? "Hello, how can I help you today?"
                        }
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {/* Modal: the dialog's scroll lock otherwise swallows the wheel over
              this portalled list, leaving it stuck. */}
          <Popover modal open={isPickerOpen} onOpenChange={setIsPickerOpen}>
            <PopoverTrigger asChild disabled={isDisabled}>
              <div
                className={cn(
                  "flex w-full items-center gap-2 rounded-md border border-border px-2 py-1.5 transition-shadow",
                  isDisabled
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer hover:shadow-sm"
                )}
              >
                <div className="flex items-center justify-center rounded-md bg-accent p-1.5 text-muted-foreground">
                  <Plus width={16} height={16} />
                </div>
                <p className="text-sm">Add language</p>
              </div>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[--radix-popover-trigger-width] p-0"
            >
              <Command>
                <CommandInput placeholder="Search languages..." />
                <CommandList>
                  <CommandEmpty>No language found.</CommandEmpty>
                  <CommandGroup>
                    {availableOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={`${option.label} ${option.value}`}
                        onSelect={() => {
                          add(option.value);
                          setIsPickerOpen(false);
                        }}
                        className="gap-2"
                      >
                        <LanguageChip tag={option.tag} />
                        <span className="truncate">{option.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </LayoutGroup>
    </BaseDialog>
  );
};
